import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useScreenSize } from '@/hooks/useMobileGestures'
import { ScoreOverview } from '@/components/score/ScoreOverview'
import { DimensionOverview } from '@/components/score/DimensionOverview'
import { RecommendationsWidget } from '@/components/recommendations/RecommendationsWidget'
import { RedFlagsPanel } from '@/components/alerts/RedFlagsPanel'
import { AlertsWidget } from '@/components/alerts/AlertsWidget'
import { BadgeWidget } from '@/components/badges/BadgeWidget'
import { MobileDashboard } from '@/components/dashboard/MobileDashboard'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { AIScoreCard, AnomalyAlert, DefaultRiskCard } from '@/components/ai'
import { businessService } from '@/services/businessService'
import { aiService } from '@/services/aiService'
import { TLBHScore, DimensionScores, Recommendation, Alert, Badge } from '@/types'

export function DashboardPage() {
  const { user } = useAuth()
  const screenSize = useScreenSize()
  const showToast = useToast()
  const [isLoading, setIsLoading] = useState(true)
  const [business, setBusiness] = useState<any>(null)
  const [scoreData, setScoreData] = useState<any>(null)
  const [redFlags, setRedFlags] = useState<any[]>([])
  const [previousScore, setPreviousScore] = useState<number>(0)
  const [aiScore, setAiScore] = useState<any>(null)
  const [aiAnomaly, setAiAnomaly] = useState<any>(null)
  const [aiDefaultRisk, setAiDefaultRisk] = useState<any>(null)
  const [aiSource, setAiSource] = useState<string>('')
  const [isLoadingAI, setIsLoadingAI] = useState(false)

  useEffect(() => {
    loadDashboardData()
  }, [])

  /**
   * Fetch AI-generated score, anomaly, and default risk for the business.
   * This automatically generates a score from the AI model even if no
   * traditional TLBH score exists yet. DEMO_MODE on the backend provides
   * realistic simulated data when the AI API is unavailable.
   */
  const loadAIScore = async (businessId: string) => {
    setIsLoadingAI(true)
    try {
      // Just read the latest stored score — don't generate new ones (cron handles generation)
      const scoreResponse = await aiService.getScore(businessId)
      
      // Handle pending state (cron hasn't run yet for this business)
      if (!scoreResponse.data || scoreResponse.source === 'pending') {
        setAiScore(null)
        setAiSource('pending')
        return
      }
      
      setAiScore(scoreResponse.data)
      setAiSource(scoreResponse.source)

      // Sync the business object's score with the AI score (prevents dual-score display)
      setBusiness((prev: any) => prev ? {
        ...prev,
        score: scoreResponse.data.score,
        band: scoreResponse.data.risk_tier === 'low' ? 'A' : scoreResponse.data.risk_tier === 'medium' ? 'B' : scoreResponse.data.risk_tier === 'high' ? 'C' : 'D',
        confidence: (Number(scoreResponse.data.confidence_interval?.upper || 80) - Number(scoreResponse.data.confidence_interval?.lower || 60)),
        calculated_at: scoreResponse.timestamp,
      } : prev)

      // Ensure confidence_interval values are numbers (DB may return strings)
      if (scoreResponse.data.confidence_interval) {
        scoreResponse.data.confidence_interval.lower = Number(scoreResponse.data.confidence_interval.lower)
        scoreResponse.data.confidence_interval.upper = Number(scoreResponse.data.confidence_interval.upper)
      }

      // Also fetch anomaly detection (non-blocking, best-effort)
      try {
        const anomalyResponse = await aiService.getAnomaly(businessId)
        setAiAnomaly(anomalyResponse.data)
      } catch (err) {
        console.warn('Anomaly detection unavailable:', err)
      }

      // Fetch default risk (non-blocking, best-effort)
      try {
        const riskResponse = await aiService.getDefaultRisk(businessId, 30)
        setAiDefaultRisk(riskResponse.data)
      } catch (err) {
        console.warn('Default risk prediction unavailable:', err)
      }
    } catch (error: any) {
      console.error('AI score fetch failed:', error)
      // Don't block the dashboard — AI score is supplementary
    } finally {
      setIsLoadingAI(false)
    }
  }

  const loadDashboardData = async () => {
    setIsLoading(true)
    try {
      console.log('Loading dashboard for user:', user)
      console.log('User businessId:', user?.businessId)
      
      // If user has a businessId, fetch that specific business
      if (user?.businessId) {
        console.log('Fetching business by ID:', user.businessId)
        try {
          const businessData = await businessService.getBusinessById(user.businessId)
          console.log('Loaded user business:', businessData)
          setBusiness(businessData)
          
          // Get score history to calculate previous score
          try {
            const historyResponse = await businessService.getScoreHistory(businessData.id)
            setScoreData(historyResponse)
            
            // Calculate previous score (second to last entry)
            if (historyResponse.history && historyResponse.history.length > 1) {
              const sortedHistory = [...historyResponse.history].sort(
                (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
              )
              setPreviousScore(parseFloat(sortedHistory[1].score))
            }
          } catch (error) {
            console.error('Failed to load score history:', error)
          }
          
          // Get red flags
          try {
            const redFlagsResponse = await businessService.getRedFlags(businessData.id)
            setRedFlags(redFlagsResponse.redFlags || [])
          } catch (error) {
            console.error('Failed to load red flags:', error)
          }

          // Fetch AI score — auto-generate if no existing score
          await loadAIScore(businessData.id)
          
          return
        } catch (error) {
          console.error('Failed to load user business:', error)
        }
      }
      
      // No businessId linked — try to find a business associated with this user
      // by searching with the user's email
      if (user?.email) {
        try {
          const searchResults = await businessService.getBusinesses({ search: user.email, limit: 1 })
          if (searchResults.businesses && searchResults.businesses.length > 0) {
            const foundBusiness = searchResults.businesses[0]
            console.log('Found business via email search:', foundBusiness)
            setBusiness(foundBusiness)
            await loadAIScore(foundBusiness.id)
            return
          }
        } catch (error) {
          console.error('Business search by email failed:', error)
        }
      }

      console.log('No business found for user, showing onboarding')
      // No business found — show the onboarding/profile completion prompt
      setBusiness(null)
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      showToast.showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load dashboard data.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading dashboard...</span>
      </div>
    )
  }

  if (!business) {
    // No business linked — show onboarding prompt OR trigger AI score if we can determine the business
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome, {user?.name || 'there'}!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Let's generate your AI-powered business health score. Please complete your business profile to get started.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">What we need:</h3>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Your PAN / GSTIN for verification</li>
              <li>• Basic business details (type, industry, turnover)</li>
              <li>• Contact information</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <a
              href="/business"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Complete Business Profile
            </a>
          </div>
        </div>

        {/* Still show AI insights if we managed to get them */}
        {isLoadingAI && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 max-w-2xl mx-auto">
            <div className="flex items-center space-x-3">
              <LoadingSpinner size="sm" />
              <span className="text-gray-600 dark:text-gray-400">Checking AI score availability...</span>
            </div>
          </div>
        )}

        {aiScore && (
          <div className="max-w-2xl mx-auto space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              AI-Powered Analysis Preview
            </h2>
            <AIScoreCard
              score={aiScore.score}
              riskTier={aiScore.risk_tier}
              confidenceInterval={aiScore.confidence_interval}
              featureImportance={aiScore.feature_importance}
              explanation={aiScore.explanation}
              isDemo={false}
            />
          </div>
        )}
      </div>
    )
  }

  // Convert business data to TLBHScore format for components
  const mockTLBHScore: TLBHScore = {
    id: business.id,
    businessId: business.id,
    tlbhScore: parseFloat(business.score || '0'),
    band: business.band,
    confidence: parseFloat(business.confidence || '0'),
    dimensionScores: {
      legal: {
        score: parseFloat(business.legal_score || '0'),
        weight: 0.25,
        explanation: 'Legal compliance score',
        verificationPoints: [],
        trend: 'stable',
        lastUpdated: business.calculated_at || business.created_at
      },
      financial: {
        score: parseFloat(business.financial_score || '0'),
        weight: 0.30,
        explanation: 'Financial health score',
        verificationPoints: [],
        trend: 'stable',
        lastUpdated: business.calculated_at || business.created_at
      },
      operational: {
        score: parseFloat(business.operational_score || '0'),
        weight: 0.25,
        explanation: 'Operational efficiency score',
        verificationPoints: [],
        trend: 'stable',
        lastUpdated: business.calculated_at || business.created_at
      },
      market: {
        score: parseFloat(business.market_score || '0'),
        weight: 0.20,
        explanation: 'Market presence score',
        verificationPoints: [],
        trend: 'stable',
        lastUpdated: business.calculated_at || business.created_at
      }
    },
    redFlags: redFlags.map(flag => ({
      id: flag.id,
      type: flag.type,
      severity: flag.severity,
      title: flag.title,
      description: flag.description,
      impact: flag.impact,
      detectedAt: flag.detected_at,
      status: flag.status,
      evidence: flag.evidence || {}
    })),
    calculatedAt: business.calculated_at || business.created_at,
    calculationVersion: '1.0.0',
    dataSourcesUsed: ['MCA', 'GSTN', 'Google Business', 'IndiaMART'],
    validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
  }

  // Mock alerts and recommendations (these would come from API in real app)
  const mockAlerts: Alert[] = []
  const mockRecommendations: Recommendation[] = []
  const mockBadges: Badge[] = []

  // If mobile or tablet, render mobile-optimized version
  if (screenSize === 'mobile' || screenSize === 'tablet') {
    return (
      <MobileDashboard
        score={mockTLBHScore}
        recommendations={mockRecommendations}
        alerts={mockAlerts}
        badges={mockBadges}
        previousScore={previousScore}
      />
    )
  }

  const scoreChange = mockTLBHScore.tlbhScore - previousScore

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Here's your business health overview for {business.business_name}.
        </p>
      </div>

      {/* Main Score Overview */}
      <ScoreOverview 
        score={mockTLBHScore}
        previousScore={previousScore}
        showTrend={true}
        layout="horizontal"
      />

      {/* AI-Powered Insights Section */}
      {aiScore && aiSource && (
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-blue-700 dark:text-blue-300">
            Score calculated from AI model • Last updated: {new Date().toLocaleDateString('en-IN')}
          </span>
          <span className="text-blue-600 dark:text-blue-400 font-medium">
            Next update: {new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      {isLoadingAI && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3">
            <LoadingSpinner size="sm" />
            <span className="text-gray-600 dark:text-gray-400">Generating AI-powered score analysis...</span>
          </div>
        </div>
      )}

      {aiScore && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            AI-Powered Analysis
          </h2>

          <AIScoreCard
            score={aiScore.score}
            riskTier={aiScore.risk_tier}
            confidenceInterval={aiScore.confidence_interval}
            featureImportance={aiScore.feature_importance}
            explanation={aiScore.explanation}
            isDemo={false}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {aiAnomaly && (
              <AnomalyAlert
                isAnomaly={aiAnomaly.is_anomaly}
                anomalyScore={aiAnomaly.anomaly_score}
                anomalyType={aiAnomaly.anomaly_type}
                severity={aiAnomaly.severity}
                contributingFeatures={aiAnomaly.contributing_features}
              />
            )}

            {aiDefaultRisk && (
              <DefaultRiskCard
                defaultProbability={aiDefaultRisk.default_probability}
                isHighRisk={aiDefaultRisk.is_high_risk}
                riskFactors={aiDefaultRisk.risk_factors}
                confidence={aiDefaultRisk.confidence}
                horizonDays={30}
              />
            )}
          </div>
        </div>
      )}

      {/* Score Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">TLBH Score</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{mockTLBHScore.tlbhScore.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-bold">{mockTLBHScore.band}</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className={scoreChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {scoreChange >= 0 ? '↗' : '↘'} {scoreChange >= 0 ? '+' : ''}{scoreChange.toFixed(2)}
              </span>
              <span className="text-gray-500 dark:text-gray-400 ml-2">from last month</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Confidence</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{mockTLBHScore.confidence.toFixed(0)}%</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <span className="text-green-600 dark:text-green-400 text-xl">✓</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-green-600 dark:text-green-400">
                {mockTLBHScore.confidence >= 80 ? 'High confidence' : mockTLBHScore.confidence >= 60 ? 'Medium confidence' : 'Low confidence'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Red Flags</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{mockTLBHScore.redFlags.length}</p>
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              mockTLBHScore.redFlags.length === 0 
                ? 'bg-green-100 dark:bg-green-900' 
                : 'bg-yellow-100 dark:bg-yellow-900'
            }`}>
              <span className={`text-xl ${
                mockTLBHScore.redFlags.length === 0 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-yellow-600 dark:text-yellow-400'
              }`}>
                {mockTLBHScore.redFlags.length === 0 ? '✓' : '⚠'}
              </span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className={mockTLBHScore.redFlags.length === 0 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-yellow-600 dark:text-yellow-400'
              }>
                {mockTLBHScore.redFlags.length === 0 ? 'All clear' : 'Needs attention'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Data Sources</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{mockTLBHScore.dataSourcesUsed.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-xl">📊</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-blue-600 dark:text-blue-400">Active sources</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dimension Analysis */}
        <div className="lg:col-span-2">
          <DimensionOverview 
            dimensions={mockTLBHScore.dimensionScores}
            defaultView="list"
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Recent Activity
            </h2>
            <div className="space-y-4">
              {scoreChange !== 0 && (
                <div className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${scoreChange > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Score updated
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Your TLBH score {scoreChange > 0 ? 'increased' : 'decreased'} by {Math.abs(scoreChange).toFixed(2)} points
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {new Date(mockTLBHScore.calculatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              {mockTLBHScore.redFlags.length > 0 && (
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {mockTLBHScore.redFlags.length} red flag{mockTLBHScore.redFlags.length > 1 ? 's' : ''} detected
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {mockTLBHScore.redFlags[0].title}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {new Date(mockTLBHScore.redFlags[0].detectedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Data sources updated
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {mockTLBHScore.dataSourcesUsed.length} sources verified
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(mockTLBHScore.calculatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Mini Score Trend */}
            {scoreData?.history && scoreData.history.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Recent Trend
                  </span>
                  <a 
                    href="/score-history" 
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    View Full History →
                  </a>
                </div>
                <div className="flex items-end space-x-1 h-16">
                  {scoreData.history.slice(-7).map((record: any, index: number) => {
                    const score = parseFloat(record.score)
                    return (
                      <div
                        key={index}
                        className="flex-1 bg-primary-200 dark:bg-primary-800 rounded-t"
                        style={{ height: `${(score / 100) * 100}%` }}
                        title={`${new Date(record.timestamp).toLocaleDateString()}: ${score.toFixed(2)}`}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Badge Widget */}
          {mockBadges.length > 0 && (
            <BadgeWidget 
              badges={mockBadges}
              maxItems={3}
              title="Recent Achievements"
            />
          )}

          {/* Red Flags Panel */}
          {mockTLBHScore.redFlags.length > 0 && (
            <RedFlagsPanel 
              redFlags={mockTLBHScore.redFlags}
              alerts={mockAlerts}
              maxItems={3}
              showFilters={false}
            />
          )}

          {/* Recommendations Widget */}
          {mockRecommendations.length > 0 && (
            <RecommendationsWidget 
              recommendations={mockRecommendations}
              maxItems={3}
            />
          )}

          {/* Alerts Widget */}
          {mockAlerts.length > 0 && (
            <AlertsWidget 
              alerts={mockAlerts}
              maxItems={3}
            />
          )}
        </div>
      </div>

      {/* Red Flags & Alerts Section */}
      {(mockTLBHScore.redFlags.length > 0 || mockAlerts.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {mockTLBHScore.redFlags.length > 0 && (
            <RedFlagsPanel 
              redFlags={mockTLBHScore.redFlags}
              showFilters={true}
              className="h-fit"
            />
          )}
          {mockAlerts.length > 0 && (
            <AlertsWidget 
              alerts={mockAlerts}
              maxItems={5}
            />
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
            <h3 className="font-medium text-gray-900 dark:text-white">Update Business Info</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Keep your business details current
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
            <h3 className="font-medium text-gray-900 dark:text-white">View Recommendations</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              See how to improve your score
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
            <h3 className="font-medium text-gray-900 dark:text-white">Download Report</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Export your TLBH score report
            </p>
          </button>
        </div>
      </div>
    </div>
  )
}